import { Link } from 'react-router-dom';
import { Download, Github, Box, BadgeCheck, ArrowRight, Code2, ExternalLink, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';

// GitHub Releases URL for the Revit plugin. When you cut a new release, the
// "/latest" suffix automatically resolves to the newest tag — no code change
// needed. Update REPO_SLUG to match your GitHub org/repo if it ever moves.
const REPO_SLUG = 'thisisradas/patinamart';
const RELEASE_URL = `https://github.com/${REPO_SLUG}/releases/latest`;
const SOURCE_URL = `https://github.com/${REPO_SLUG}/tree/main/revit-plugin`;

export default function RevitDownloadPage() {
  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="revit-download-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-accent mb-3 inline-flex items-center gap-2">
            <Box className="w-3 h-3" /> PatinaMart for Revit
          </p>
          <h1 className="font-playfair text-4xl md:text-6xl font-bold text-foreground leading-tight mb-5">
            Drag salvaged stock straight
            <br />
            <span className="text-accent">into your Revit project.</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
            The PatinaMart plugin adds a panel inside Revit so you can search the marketplace,
            preview embodied-carbon savings, and drop reclaimed materials into your model — without
            switching browsers or fudging family files.
          </p>
        </div>

        {/* Download CTAs */}
        <div className="rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/10 to-transparent p-7 mb-10">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-11 h-11 rounded-lg bg-accent/15 border border-accent/40 flex items-center justify-center flex-shrink-0">
              <Download className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h2 className="font-playfair text-2xl font-bold text-foreground mb-1">Download the latest release</h2>
              <p className="text-sm text-muted-foreground">
                Free • Revit 2023, 2024, 2025 supported • Windows only (Revit doesn't run on macOS)
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a href={RELEASE_URL} target="_blank" rel="noreferrer" data-testid="revit-download-cta">
              <Button className="btn-gold">
                <Download className="w-4 h-4 mr-2" />
                Download from GitHub Releases
              </Button>
            </a>
            <a href={SOURCE_URL} target="_blank" rel="noreferrer">
              <Button variant="outline" className="border-border/40">
                <Code2 className="w-4 h-4 mr-2" />
                View source on GitHub
              </Button>
            </a>
          </div>
        </div>

        {/* Install instructions */}
        <h3 className="font-playfair text-2xl font-bold text-foreground mb-6">Install in three steps</h3>
        <ol className="space-y-5 mb-12">
          {STEPS.map((step, i) => (
            <li key={step.title} className="flex gap-4">
              <div className="w-9 h-9 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center flex-shrink-0 text-accent font-semibold">
                {i + 1}
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1">{step.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
                {step.code && (
                  <pre className="mt-2 rounded-md border border-border/30 bg-background/40 p-3 text-xs overflow-x-auto"><code>{step.code}</code></pre>
                )}
              </div>
            </li>
          ))}
        </ol>

        {/* What's inside */}
        <h3 className="font-playfair text-2xl font-bold text-foreground mb-6">What you get</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-xl border border-border/20 bg-card/40 p-5">
              <div className="flex items-center gap-2 mb-2">
                <BadgeCheck className="w-4 h-4 text-accent" />
                <h4 className="font-medium text-foreground">{f.title}</h4>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>

        {/* Need help */}
        <div className="rounded-2xl border border-border/20 bg-card/30 p-6 flex items-start gap-4">
          <Mail className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground mb-1">Stuck on installation?</p>
            <p className="text-sm text-muted-foreground">
              Email <a className="text-accent hover:underline" href="mailto:contact@patinamart.com">contact@patinamart.com</a> with your Revit version and the error and we'll help you out — usually within a working day.
            </p>
          </div>
        </div>

        {/* Companion CTAs */}
        <div className="text-center mt-14">
          <p className="text-sm text-muted-foreground mb-3">More PatinaMart tools</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Link to="/marketplace">
              <Button variant="outline" className="border-border/40">
                Browse the marketplace <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/supplier-app">
              <Button variant="outline" className="border-border/40">
                Supplier Wingman <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/sketchup">
              <Button variant="outline" className="border-border/40">
                Using SketchUp instead? <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const STEPS = [
  {
    title: 'Download the .zip from GitHub Releases',
    body: 'Click the gold button above and grab the latest release. The zip contains the plugin DLL, the .addin manifest, an example config, and a one-click PowerShell installer.',
  },
  {
    title: 'Right-click Install-PatinaMart.ps1 → Run with PowerShell',
    body: 'The script copies everything into your Revit Addins folder AND registers the patinamart-revit:// URL scheme so "Open in Revit" buttons on the website launch the plugin straight to a listing. Runs entirely under your user account — no admin password required.',
    code: 'powershell -ExecutionPolicy Bypass -File .\\Install-PatinaMart.ps1',
  },
  {
    title: 'Launch Revit and pair your account',
    body: 'Open Revit 2025. You\'ll see the new PatinaMart ribbon tab. Click Settings the first time to set your default name + email so enquiries auto-fill. From the website, every listing now has an "Open in Revit" button — clicking it opens that listing inside the plugin (with your browser asking permission the first time).',
  },
];

const FEATURES = [
  {
    title: 'Open any listing from the web in Revit',
    body: 'On every PatinaMart listing page, an "Open in Revit" button deep-links straight into the plugin. If Revit is already running it just pops the listing; if not, the plugin launches Revit for you.',
  },
  {
    title: 'Live marketplace search inside Revit',
    body: 'Type "reclaimed brass tap" — the plugin shows live PatinaMart listings without leaving Revit.',
  },
  {
    title: 'Drag-and-drop into your model',
    body: 'Click any result and it drops into your project as a parametric family with the supplier, price, and provenance baked into the family parameters.',
  },
  {
    title: 'Carbon savings auto-tagged',
    body: 'Every dropped item carries its embodied-carbon avoidance. Your project schedule rolls up the total — drop straight into a Sustainability Report.',
  },
  {
    title: 'Find Similar in one click',
    body: 'Right-click any family in your project → "Find similar reclaimed". The plugin matches dimensions and material to surface real PatinaMart stock.',
  },
];
